import posthog from "posthog-js";
import type { BandPriceChipTier } from "@/lib/pricing/sellerPriceBandRecommendations";
import { classifyEnforcedBandPrice } from "@/lib/pricing/sellerPriceBandCheck";
import { SELLER_PRICE_BAND_ERROR_CODES } from "@/lib/pricing/sellerPriceBandErrors";

/** Client PostHog events for seller pricing friction (Phases 2–3). */

export type PriceVsBand = "below" | "in" | "at_max" | "unknown";

export type ListingPriceBlockedMode = "single" | "bulk" | "edit";

type CategoryProps = {
  category: string;
  subcategory?: string | null;
  enforcementActive?: boolean;
  chipsEnabled?: boolean;
};

type SessionFlags = {
  hadPriceSuggestion: boolean;
  usedPriceChip: boolean;
};

const sessionFlags: SessionFlags = {
  hadPriceSuggestion: false,
  usedPriceChip: false,
};

/** Test / navigation helper — resets in-memory submit enrichment flags. */
export function resetSellerPricingFrictionSessionFlags(): void {
  sessionFlags.hadPriceSuggestion = false;
  sessionFlags.usedPriceChip = false;
}

export function getSellerPricingFrictionSessionFlags(): Readonly<SessionFlags> {
  return { ...sessionFlags };
}

function capture(event: string, properties: Record<string, unknown>): void {
  try {
    posthog.capture(event, properties);
  } catch {
    // Never block sell UX on analytics
  }
}

export function capturePriceSuggestionShown(props: CategoryProps): void {
  sessionFlags.hadPriceSuggestion = true;
  capture("price_suggestion_shown", {
    category: props.category,
    subcategory: props.subcategory || null,
    enforcement_active: Boolean(props.enforcementActive),
    chips_enabled: Boolean(props.chipsEnabled),
  });
}

export function capturePriceSuggestionAccepted(params: {
  category: string;
  subcategory?: string | null;
  suggestedPrice: number;
  applyMode: "auto" | "explicit";
  enforcementActive?: boolean;
  chipsEnabled?: boolean;
}): void {
  sessionFlags.hadPriceSuggestion = true;
  capture("price_suggestion_accepted", {
    category: params.category,
    subcategory: params.subcategory || null,
    suggested_price: params.suggestedPrice,
    apply_mode: params.applyMode,
    enforcement_active: Boolean(params.enforcementActive),
    chips_enabled: Boolean(params.chipsEnabled),
  });
}

export function capturePriceSuggestionDismissed(props: CategoryProps): void {
  capture("price_suggestion_dismissed", {
    category: props.category,
    subcategory: props.subcategory || null,
    enforcement_active: Boolean(props.enforcementActive),
    chips_enabled: Boolean(props.chipsEnabled),
  });
}

export function capturePriceChipClicked(params: {
  category: string;
  subcategory?: string | null;
  chipType: BandPriceChipTier;
  price: number;
  enforcementActive?: boolean;
  chipsEnabled?: boolean;
}): void {
  sessionFlags.usedPriceChip = true;
  sessionFlags.hadPriceSuggestion = true;
  capture("price_chip_clicked", {
    category: params.category,
    subcategory: params.subcategory || null,
    chip_type: params.chipType,
    applied_price: params.price,
    enforcement_active: Boolean(params.enforcementActive),
    chips_enabled: Boolean(params.chipsEnabled),
  });
}

export function captureListingPriceBlockedClient(params: {
  listingMode: ListingPriceBlockedMode;
  category: string;
  subcategory?: string | null;
  attemptedPrice: number;
  bandMin: number;
  bandMax: number;
  sampleCount?: number;
}): void {
  capture("listing_price_blocked_client", {
    listing_mode: params.listingMode,
    category: params.category,
    subcategory: params.subcategory || null,
    attempted_price: params.attemptedPrice,
    band_min: params.bandMin,
    band_max: params.bandMax,
    sample_count: params.sampleCount ?? null,
    code: SELLER_PRICE_BAND_ERROR_CODES.PRICE_OUT_OF_BAND,
  });
}

export function resolvePriceVsBand(
  priceNaira: number,
  band: { min: number; max: number } | null | undefined,
): PriceVsBand {
  if (!band || !(band.max > 0)) return "unknown";
  const status = classifyEnforcedBandPrice(priceNaira, band);
  if (status === "below_band") return "below";
  if (status === "above_band") return "unknown";
  if (priceNaira === band.max) return "at_max";
  return "in";
}

/** Props to merge into listing_submitted / bulk_listing_submitted. */
export function buildListingSubmittedPricingProps(params: {
  enforcementActive: boolean;
  chipsEnabled: boolean;
  price: string | number;
  band: { min: number; max: number } | null | undefined;
}): Record<string, unknown> {
  const numericPrice =
    typeof params.price === "number"
      ? params.price
      : Number(String(params.price).replace(/,/g, ""));
  const flags = getSellerPricingFrictionSessionFlags();
  return {
    enforcement_active: params.enforcementActive,
    chips_enabled: params.chipsEnabled,
    used_price_chip: flags.usedPriceChip,
    had_price_suggestion: flags.hadPriceSuggestion,
    price_vs_band: Number.isFinite(numericPrice)
      ? resolvePriceVsBand(numericPrice, params.band)
      : "unknown",
  };
}
